import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_STYLE_VTON_IMAGE } from 'graph_ql/style_vton_image/mutations';
import { VtonImagePoseType } from './type';

interface UseTryOnGenerationProps {
    style: any;
    item: any;
    userId: string;
    onCompleted?: (url: string, poseType: VtonImagePoseType, taffiId: string) => void;
}

export const useTryOnGeneration = ({ style, item, userId, onCompleted }: UseTryOnGenerationProps) => {
    const [createStyleVtonImageMutation, { loading: loadingVtonImage }] = useMutation(CREATE_STYLE_VTON_IMAGE, {
        context: {
            allowAnonymousAuth: true,
        },
        onCompleted(response) {
            if (onCompleted) {
                const taffiId = response.createStyleVtonImage.taffiId;
                onCompleted(response.createStyleVtonImage.url, response.createStyleVtonImage.poseType, taffiId);
            }
        },
        onError(error) {
            console.error('Error creating style VTON image:', error);
        },
    });

    const generateTryOnImage = useCallback(
        ({ modelImageUrl, forceUpdate = false }: { modelImageUrl: string; forceUpdate?: boolean }) => {
            if (!modelImageUrl) {
                console.warn('No model image URL provided');
                return;
            }

            let styleItems: { category: string | null; url: string }[] = [];
            let payload: any;

            if (item) {
                styleItems = [
                    {
                        category: null,
                        url: item.value,
                    },
                ];
                payload = {
                    itemId: item.id || item.productId,
                    itemTaffiId: item.taffiId || item.productTaffiId,
                    userId,
                    userImageUrl: modelImageUrl,
                    styleItems,
                    forceUpdate,
                };
            } else {
                // Create a map of item id to category
                const mapItemIdToCategory = new Map<string, string>();
                style?.items?.forEach((item: any) => {
                    // Amira screen
                    if (item?.category) {
                        mapItemIdToCategory.set(item.id, item?.category);
                    }
                    // Style detail screen
                    if (item?.id && item?.data?.metadata?.category) {
                        mapItemIdToCategory.set(item.id, item?.data?.metadata?.category);
                    }
                });

                const styleItems = style?.imageJson?.reduce((acc: { category: string; url: string }[], item: any) => {
                    const category = mapItemIdToCategory.get(item.itemId);
                    if (item.imageCanvas && category) {
                        acc.push({
                            category,
                            url: item.imageCanvas,
                        });
                    }
                    return acc;
                }, []);

                if (!styleItems || !styleItems.length) {
                    console.warn('No style images found in style.imageJson');
                    return;
                }

                payload = {
                    styleId: style.id || style.productId,
                    styleTaffiId: style.taffiId || style.productTaffiId,
                    userId,
                    userImageUrl: modelImageUrl,
                    styleItems,
                    forceUpdate,
                };
            }

            createStyleVtonImageMutation({ variables: { input: payload } });
        },
        [createStyleVtonImageMutation, item, style, userId],
    );

    return {
        generateTryOnImage,
        loadingVtonImage,
    };
};
